import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, HeartPulse, Sprout } from "lucide-react";
import { IMG } from "@/lib/content";

const COMMON = [
  ["/", "Accueil"], ["/ma-leba", "Ma Leba"], ["/actualites", "Actualités"], ["/galerie", "Galerie"],
  ["/devenir-partenaire", "Partenaire"], ["/communication", "Communication"], ["/contact", "Contact"],
];
const SUB = {
  ong: [["/ong", "Présentation"], ["/ong/activites-sante", "Santé"], ["/ong/principes", "Principes"], ["/ong/campagnes", "Campagnes"],
    ["/ong/partenaires-etrangers", "Partenaires étrangers"], ["/ong/bureau", "Bureau exécutif"], ["/ong/documents", "Documents"]],
  association: [["/association", "Présentation"], ["/association/pole-agricole", "Pôle agricole"], ["/association/formation", "Centre de formation"],
    ["/association/bureau", "Bureau exécutif"], ["/association/documents", "Documents"]],
};

const slug = (p) => p.replace(/\//g, "-").replace(/^-/, "") || "accueil";

function UniverseSwitch({ universe, onPick }) {
  const base = "flex items-center gap-1.5 rounded-full px-3 py-1.5 font-display text-sm font-semibold uppercase tracking-wide transition-colors";
  return (
    <div className="flex items-center gap-1 rounded-full bg-neutral-100 p-1" role="group" aria-label="Choisir un univers">
      <Link to="/ong" onClick={onPick} data-testid="universe-switch-ong" aria-current={universe === "ong"}
        className={`${base} ${universe === "ong" ? "bg-ong text-white" : "text-ong hover:bg-white"}`}>
        <HeartPulse className="h-4 w-4" aria-hidden="true" /> ONG
      </Link>
      <Link to="/association" onClick={onPick} data-testid="universe-switch-association" aria-current={universe === "association"}
        className={`${base} ${universe === "association" ? "bg-asso text-white" : "text-asso hover:bg-white"}`}>
        <Sprout className="h-4 w-4" aria-hidden="true" /> Association
      </Link>
    </div>
  );
}

export function Header({ universe }) {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const sub = SUB[universe];
  const linkCls = ({ isActive }) =>
    `px-2.5 py-2 font-display text-[15px] font-semibold uppercase tracking-wide transition-colors ${isActive ? "text-u" : "text-neutral-700 hover:text-u"}`;
  return (
    <header className="sticky top-0 z-40 border-b border-black/5 bg-white/90 backdrop-blur-md" data-testid="site-header">
      <div className="container-x flex h-16 items-center justify-between gap-4 lg:h-[72px]">
        <Link to="/" className="flex items-center gap-2" data-testid="header-logo-link" aria-label="Accueil MA LEBA">
          <img src={IMG.logoOng} alt="Logo ONG MA LEBA" className="h-10 w-10 object-contain lg:h-12 lg:w-12" width="48" height="48" />
          <img src={IMG.logoAsso} alt="Logo ASSOCIATION MA LEBA" className="h-10 w-10 object-contain lg:h-12 lg:w-12" width="48" height="48" />
          <span className="font-display text-xl font-bold uppercase tracking-tight text-ink">Ma Leba</span>
        </Link>
        <nav className="hidden items-center xl:flex" aria-label="Navigation principale">
          {COMMON.map(([to, label]) => (
            <NavLink key={to} to={to} end={to === "/"} className={linkCls} data-testid={`nav-${slug(to)}`}>{label}</NavLink>
          ))}
        </nav>
        <div className="hidden md:block"><UniverseSwitch universe={universe} /></div>
        <button className="rounded-md p-2 xl:hidden" onClick={() => setOpen(!open)} aria-expanded={open} aria-label="Menu" data-testid="mobile-menu-toggle">
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>
      {sub && (
        <div className="bg-u text-white">
          <nav className="container-x flex gap-1 overflow-x-auto py-1.5" aria-label="Navigation de l'univers">
            {sub.map(([to, label]) => (
              <NavLink key={to} to={to} end data-testid={`subnav-${slug(to)}`}
                className={({ isActive }) => `whitespace-nowrap rounded-full px-3 py-1.5 font-display text-sm font-semibold uppercase tracking-wide transition-colors ${isActive ? "bg-white text-u" : "hover:bg-white/15"}`}>
                {label}
              </NavLink>
            ))}
          </nav>
        </div>
      )}
      {open && (
        <div className="border-t border-black/5 bg-white xl:hidden" data-testid="mobile-menu">
          <div className="container-x space-y-4 py-5">
            <div className="md:hidden"><UniverseSwitch universe={universe} onPick={() => setOpen(false)} /></div>
            <nav className="grid grid-cols-2 gap-1" aria-label="Navigation mobile">
              {COMMON.map(([to, label]) => (
                <NavLink key={to} to={to} end={to === "/"} onClick={() => setOpen(false)} className={linkCls} data-testid={`mobile-nav-${slug(to)}`}>
                  {label}
                </NavLink>
              ))}
            </nav>
            <p className="sr-only">{pathname}</p>
          </div>
        </div>
      )}
    </header>
  );
}
